import { useState, useEffect } from "react";
import { Trophy, XCircle } from "lucide-react";
import { useDropdowns } from "@/hooks/useMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ReasonDialog({ open, onOpenChange, kind = "won", title, initialReason = "", initialComments = "", onConfirm, saving = false, confirmLabel }) {
  const { data: dd } = useDropdowns();
  const [reason, setReason] = useState(initialReason);
  const [comments, setComments] = useState(initialComments);
  useEffect(() => { if (open) { setReason(initialReason || ""); setComments(initialComments || ""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const options = (kind === "won" ? dd?.won_reason : dd?.lost_reason) || [];
  const needsComment = reason === "Other" && !comments.trim();
  const won = kind === "won";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reason-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {won ? <Trophy className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
            {title || (won ? "Why did we win?" : "Why was it lost?")}
          </DialogTitle>
          <DialogDescription>Record why — this powers the Win/Loss analysis in the BI module.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">{won ? "Won Reason" : "Lost Reason"} <span className="text-red-500">*</span></Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-9" data-testid="reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>{options.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Comments {reason === "Other" && <span className="text-red-500">*</span>}</Label>
            <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={2}
              placeholder={reason === "Other" ? "Describe the reason" : "Optional context (competitor, price, spec…)"} data-testid="reason-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reason-cancel">Cancel</Button>
          <Button className={won ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
            disabled={!reason || needsComment || saving} onClick={() => onConfirm(reason, comments)} data-testid="reason-confirm">
            {confirmLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
